import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { PlayCircle, X, Bot } from "lucide-react";
import { usePreferences } from "../../context/PreferencesContext";
import { NAV_GROUPS, badgeColor, LIVE_DEMO, LIVE_DEMO_URL, BRAND_NAME } from "./navConfig";

// ── component ────────────────────────────────────────────────────
// The mobile/tablet slide-in drawer. Desktop's primary navigation is the
// horizontal MenuBar; this only renders below the `lg` breakpoint, where a
// one-line dropdown bar has nowhere to put a hover panel and a vertical
// list behind a hamburger is the right answer instead — the same split
// BMC Intelligence's own layout uses (MenuBar on wide screens, this rail
// as a drawer everywhere else).
export default function Sidebar({ open, setOpen }) {
  const location = useLocation();
  const { lang } = usePreferences();

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 bg-slate-900/30 z-40 lg:hidden"
          onClick={() => setOpen(false)}
        />
      )}
      <div
        className={`fixed top-0 left-0 bottom-0 w-72 max-w-[85vw] z-50 lg:hidden shadow-2xl transition-transform duration-200 ease-out bg-white flex flex-col ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-100 flex-shrink-0">
          <span className="font-black text-sm text-slate-900">{BRAND_NAME[lang]}</span>
          <button
            onClick={() => setOpen(false)}
            className="p-2 rounded-xl hover:bg-slate-100 transition-colors text-slate-500"
            aria-label="Close navigation"
          >
            <X size={18} />
          </button>
        </div>

        <nav aria-label="Primary navigation" className="flex-1 overflow-y-auto px-3 py-4 space-y-5">
          {NAV_GROUPS.map((group, gi) => (
            <div key={group.key ?? `top-${gi}`}>
              {group.heading && (
                <div className="px-2.5 mb-1.5 text-[10px] font-black uppercase tracking-wider text-slate-400">
                  {group.heading[lang]}
                </div>
              )}
              <ul className="space-y-0.5">
                {group.items.map(item => {
                  const Icon = item.icon;
                  const active = location.pathname === item.path;
                  return (
                    <li key={item.path}>
                      <NavLink
                        to={item.path}
                        onClick={() => setOpen(false)}
                        title={item.label[lang]}
                        className={`group relative flex items-center gap-2.5 rounded-xl pl-3 pr-2.5 py-2 text-[13px] font-semibold transition-colors duration-150 ${
                          active ? "bg-saffron/10 text-saffron-dark" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                        }`}
                      >
                        <span
                          aria-hidden
                          className={`absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[3px] rounded-full transition-opacity ${
                            active ? "bg-saffron-dark opacity-100" : "opacity-0"
                          }`}
                        />
                        <Icon size={16} className={`flex-shrink-0 ${active ? "text-saffron-dark" : "text-slate-400 group-hover:text-slate-600"}`} />
                        <span className="flex-1 min-w-0 truncate">{item.label[lang]}</span>
                        {item.badge && (
                          <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-full leading-none flex-shrink-0 ${badgeColor(item.badge)}`}>
                            {item.badge}
                          </span>
                        )}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>

        <div className="flex-shrink-0 border-t border-slate-100 p-3 space-y-0.5">
          {/* Kumbh Sathi sits beside Live Demo here as well as in the header.
              The header button is desktop-only (hidden md:flex), and Kumbh
              Sathi is no longer in the nav list, so without this entry there
              is no way to reach it on a phone. */}
          <NavLink
            to="/sahayak"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2.5 rounded-xl px-3 py-2 text-[13px] font-semibold text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors"
          >
            <Bot size={16} className="flex-shrink-0 text-saffron-dark" />
            <span>{lang === "mr" ? "कुंभ साथी" : "Kumbh Sathi"}</span>
          </NavLink>

          <a
            href={LIVE_DEMO_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2.5 rounded-xl px-3 py-2 text-[13px] font-semibold text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors"
          >
            <PlayCircle size={16} className="flex-shrink-0 text-slate-400" />
            <span>{LIVE_DEMO[lang]}</span>
          </a>
        </div>
      </div>
    </>
  );
}
